import {
  ETALON_OBJECT_TYPE_OPTIONS,
  hasEtalonEndTypeExpectation,
  isKnownObjectType,
  isSkeyRequiredForObjectType,
} from '../../shared/config/pcfEtalonCatalog';
import {
  filterObjectTypeOptionsByValues,
  filterObjectTypeOptionsForCategory,
  parseObjectTypeValues,
} from '../../shared/config/objectTypeCategoryCompatibility';
import { normalizeValidationId } from './validationConstants';

export type QuickFixType = 'autoDetect' | 'dropdown' | 'input' | 'dualInput';

export interface QuickFixConfig {
  label: string;
  type: QuickFixType;
  parameterName: string;
  canAutoFix: boolean;
  fixType: 'setParameter' | 'autoDetectObjectType' | 'autoResolveEndTypes' | 'assignRepairPipingSystem';
  description: string;
  options?: { label: string; value: string }[];
  placeholder?: string;
  fields?: { parameterName: string; label: string; placeholder: string }[];
  messageFilter?: string;
  /** Only offer the fix when the issue carries at least one target element id. */
  requiresElements?: boolean;
}

const OBJECT_TYPE_OPTIONS: { label: string; value: string }[] = ETALON_OBJECT_TYPE_OPTIONS
  .filter((option) => option.value !== '#PCF_OBJECT_TYPE');

const quickFixMap: Record<number, QuickFixConfig[]> = {
  // ValidationId #2 Required Parameters Presence
  2: [
    {
      label: 'Auto-detect OBJECT-TYPE',
      type: 'autoDetect',
      parameterName: 'OBJECT-TYPE',
      canAutoFix: true,
      fixType: 'autoDetectObjectType',
      description: 'Automatically determines the OBJECT-TYPE from the Revit category and connector data.',
      messageFilter: 'OBJECT-TYPE',
    },
    {
      label: 'Add OBJECT-TYPE',
      type: 'dropdown',
      parameterName: 'OBJECT-TYPE',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign an OBJECT-TYPE value.',
      options: OBJECT_TYPE_OPTIONS,
      messageFilter: 'OBJECT-TYPE',
    },
    {
      label: 'Add SKEY',
      type: 'input',
      parameterName: 'SKEY',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign an SKEY value to the mapped Revit parameter.',
      placeholder: 'e.g., EL90, FLWN, TE90',
      messageFilter: 'SKEY',
    },
  ],

  // ValidationId #3 Unsupported OBJECT-TYPE
  3: [
    {
      label: 'Add OBJECT-TYPE',
      type: 'dropdown',
      parameterName: 'OBJECT-TYPE',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign an OBJECT-TYPE value.',
      options: OBJECT_TYPE_OPTIONS,
    },
  ],

  // ValidationId #4 OBJECT-TYPE category mismatch. Display priority currently shows this as Validation #7.
  4: [
    {
      label: 'Auto-detect OBJECT-TYPE',
      type: 'autoDetect',
      parameterName: 'OBJECT-TYPE',
      canAutoFix: true,
      fixType: 'autoDetectObjectType',
      description: 'Automatically determines the OBJECT-TYPE from the Revit category and connector data.',
    },
    {
      label: 'Add OBJECT-TYPE',
      type: 'dropdown',
      parameterName: 'OBJECT-TYPE',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign an OBJECT-TYPE value.',
      options: OBJECT_TYPE_OPTIONS,
    },
  ],

  // ValidationId #7 OBJECT-TYPE connector-count mismatch
  7: [
    {
      label: 'Auto-detect OBJECT-TYPE',
      type: 'autoDetect',
      parameterName: 'OBJECT-TYPE',
      canAutoFix: true,
      fixType: 'autoDetectObjectType',
      description: 'Automatically determines the OBJECT-TYPE from the Revit category and connector data.',
      messageFilter: 'OBJECT-TYPE',
    },
    {
      label: 'Add OBJECT-TYPE',
      type: 'dropdown',
      parameterName: 'OBJECT-TYPE',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign an OBJECT-TYPE value.',
      options: OBJECT_TYPE_OPTIONS,
      messageFilter: 'OBJECT-TYPE',
    },
  ],

  // ValidationId #8 System assignment (displayed as Validation #12): host-side repair that copies
  // a piping system type as PipeEX-RepairFlow and assigns components to new numbered systems.
  8: [
    {
      label: 'Assign PipeEX-RepairFlow system',
      type: 'autoDetect',
      parameterName: '',
      canAutoFix: true,
      fixType: 'assignRepairPipingSystem',
      description:
        'Creates a copy of the piping system type named PipeEX-RepairFlow and assigns each component to a newly numbered system.',
      requiresElements: true,
    },
  ],

  // #11 END-TYPE consistency
  11: [
    {
      label: 'Auto-resolve END-TYPE',
      type: 'autoDetect',
      parameterName: 'END-TYPE_1',
      canAutoFix: true,
      fixType: 'autoResolveEndTypes',
      description: 'Automatically determine the END-TYPE value from the OBJECT-TYPE/SKEY etalon rows.',
    },
  ],

  // #13 PIPING-SPEC completion
  13: [
    {
      label: 'Add PIPING-SPEC',
      type: 'input',
      parameterName: 'PIPING-SPEC',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign a PIPING-SPEC value to the mapped Revit parameter.',
      placeholder: 'e.g., CS150, SS-300',
    },
  ],

  // #15 SKEY
  15: [
    {
      label: 'Add SKEY',
      type: 'input',
      parameterName: 'SKEY',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign an SKEY value to the mapped Revit parameter.',
      placeholder: 'e.g., EL90, FLWN, TE90',
    },
  ],

  // #17 Insulation spec
  17: [
    {
      label: 'Add INSULATION-SPEC',
      type: 'input',
      parameterName: 'INSULATION-SPEC',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign an INSULATION-SPEC value to the mapped Revit parameter.',
      placeholder: 'e.g., PP, HC, AC',
    },
  ],

  // #18 Insulation thickness
  18: [
    {
      label: 'Add INSULATIONTHICKNESS',
      type: 'input',
      parameterName: 'INSULATIONTHICKNESS',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign an INSULATIONTHICKNESS value to the mapped Revit parameter.',
      placeholder: 'e.g. 25, 50, 100',
    },
  ],

  // #19 Tracing spec
  19: [
    {
      label: 'Add TRACING-SPEC',
      type: 'input',
      parameterName: 'TRACING-SPEC',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign a TRACING-SPEC value to the mapped Revit parameter.',
      placeholder: 'e.g., Steam, Electric, Hot Water, None',
    },
  ],

  // #20 Bolt set handling
  20: [
    {
      label: 'Add bolt length',
      type: 'input',
      parameterName: 'BOLT-LENGTH',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign a bolt length value to the mapped Revit parameter.',
      placeholder: 'e.g. 100, 150',
      messageFilter: 'BOLT-LENGTH',
    },
    {
      label: 'Add bolt diameter',
      type: 'input',
      parameterName: 'BOLT-DIA',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign a bolt diameter value to the mapped Revit parameter.',
      placeholder: 'e.g., M16, M20',
      messageFilter: 'BOLT-DIA',
    },
  ],

  // #26 PIPE-SPEC consistency
  26: [
    {
      label: 'Add PIPE-SPEC',
      type: 'input',
      parameterName: 'PIPE-SPEC',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign a PIPE-SPEC value to the mapped Revit parameter.',
      placeholder: 'e.g., CS150, SS-300',
    },
  ],

  // #33 Object Type vs End Type
  33: [
    {
      label: 'Auto-resolve END-TYPE',
      type: 'autoDetect',
      parameterName: 'END-TYPE_1',
      canAutoFix: true,
      fixType: 'autoResolveEndTypes',
      description: 'Automatically determine the END-TYPE value from the OBJECT-TYPE/SKEY etalon rows.',
    },
  ],

  // #35 Object Type/SKEY readiness
  35: [
    {
      label: 'Add OBJECT-TYPE',
      type: 'dropdown',
      parameterName: 'OBJECT-TYPE',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign an OBJECT-TYPE value.',
      options: OBJECT_TYPE_OPTIONS,
      messageFilter: 'OBJECT-TYPE',
    },
    {
      label: 'Add SKEY',
      type: 'input',
      parameterName: 'SKEY',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign an SKEY value to the mapped Revit parameter.',
      placeholder: 'e.g., ELBW, FLWN, VGFL',
      messageFilter: 'SKEY',
    },
  ],

  // #34 Required export mapping keys
  34: [
    {
      label: 'Add mapped parameter value',
      type: 'input',
      parameterName: 'Mapped parameter',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Manually assign a value to the mapped Revit parameter.',
      placeholder: 'Enter value',
      messageFilter: 'HAS NO VALUES',
    },
    {
      label: 'Fill a missing BOM field value',
      type: 'input',
      parameterName: 'Mapped parameter',
      canAutoFix: false,
      fixType: 'setParameter',
      description: 'Open the dialog, choose or enter the new Bill of Materials (BOM) value, review it with the team if necessary, then confirm the planned change.',
      placeholder: 'Enter value',
      messageFilter: 'IS EMPTY',
    },
  ],
};

type IssueMessageClass = 'missing' | 'invalid' | 'other';

function classifyIssueMessage(message: string): IssueMessageClass {
  const upperMessage = message.toUpperCase();
  const looksMissing =
    upperMessage.includes('MISSING') ||
    upperMessage.includes('NOT DEFINED') ||
    upperMessage.includes('NO VALUE') ||
    upperMessage.includes('NO VALUES') ||
    upperMessage.includes('IS EMPTY') ||
    upperMessage.includes('EMPTY');

  if (looksMissing) {
    return 'missing';
  }

  const looksInvalid =
    upperMessage.includes('INVALID') ||
    upperMessage.includes('UNSUPPORTED') ||
    upperMessage.includes('MISMATCH') ||
    upperMessage.includes('NON STANDARD') ||
    upperMessage.includes('DOES NOT MATCH') ||
    upperMessage.includes("DOESN'T MATCH") ||
    upperMessage.includes('DOESNT MATCH') ||
    upperMessage.includes('UNUSUAL') ||
    upperMessage.includes('NOT NUMERIC') ||
    upperMessage.includes('OUT OF EXPECTED RANGE') ||
    upperMessage.includes('PLACEHOLDER');

  if (looksInvalid) {
    return 'invalid';
  }

  return 'other';
}

function adaptFixLabelToMessage(fix: QuickFixConfig, messageClass: IssueMessageClass): QuickFixConfig {
  if (fix.fixType !== 'setParameter') {
    return fix;
  }

  if (!fix.label.startsWith('Set ')) {
    return fix;
  }

  if (messageClass === 'missing') {
    return { ...fix, label: fix.label.replace(/^Set /, 'Add ') };
  }

  if (messageClass === 'invalid') {
    return { ...fix, label: fix.label.replace(/^Set /, 'Update ') };
  }

  return fix;
}

function getCategoryMatchObjectTypeOptions(additionalData?: Record<string, string>): { label: string; value: string }[] {
  const expectedObjectTypes = parseObjectTypeValues(additionalData?.expectedObjectTypes);
  if (expectedObjectTypes.length > 0) {
    return filterObjectTypeOptionsByValues(OBJECT_TYPE_OPTIONS, expectedObjectTypes)
      .filter((option) => option.value !== 'IGNORE');
  }

  return filterObjectTypeOptionsForCategory(OBJECT_TYPE_OPTIONS, additionalData?.categoryName)
    .filter((option) => option.value !== 'IGNORE');
}

function adaptFixToIssueContext(
  fix: QuickFixConfig,
  normalizedValidationId: number,
  messageClass: IssueMessageClass,
  additionalData?: Record<string, string>
): QuickFixConfig {
  const adaptedFix = adaptFixLabelToMessage(fix, messageClass);
  if (
    normalizedValidationId !== 4 ||
    adaptedFix.type !== 'dropdown' ||
    adaptedFix.parameterName.trim().toUpperCase() !== 'OBJECT-TYPE'
  ) {
    return adaptedFix;
  }

  return {
    ...adaptedFix,
    options: getCategoryMatchObjectTypeOptions(additionalData),
  };
}

function isValueCoverageIssue(validationId: number, upperMessage: string): boolean {
  if (normalizeValidationId(validationId) !== 34) {
    return false;
  }

  return upperMessage.includes('HAS NO VALUES') || upperMessage.includes('IS EMPTY');
}

function getAdditionalDataParameterName(additionalData?: Record<string, string>): string {
  return (
    additionalData?.parameterName ||
    additionalData?.skeyParameterName ||
    ''
  ).trim().toUpperCase();
}

function extractIssueObjectType(
  message: string,
  additionalData?: Record<string, string>
): string {
  const explicitObjectType = (
    additionalData?.objectType ||
    additionalData?.objectTypeValue ||
    additionalData?.currentObjectType ||
    ''
  ).trim();

  if (explicitObjectType) {
    return explicitObjectType;
  }

  return message.match(/OBJECT-TYPE\s+['"]([^'"]+)['"]/i)?.[1]?.trim() ?? '';
}

function extractIssueSkey(
  message: string,
  additionalData?: Record<string, string>
): string {
  const explicitSkey = (
    additionalData?.skey ||
    additionalData?.currentSkey ||
    additionalData?.actualSkey ||
    ''
  ).trim();

  if (explicitSkey && !/^<empty>$/i.test(explicitSkey)) {
    return explicitSkey;
  }

  const messageSkey = message.match(/SKEY\s+['"]([^'"]+)['"]/i)?.[1]?.trim() ?? '';
  return /^<empty>$/i.test(messageSkey) ? '' : messageSkey;
}

function isMissingSkeyIssue(
  normalizedValidationId: number,
  upperMessage: string,
  additionalData?: Record<string, string>
): boolean {
  if (!upperMessage.includes('SKEY')) {
    return false;
  }

  if (upperMessage.includes('MISSING SKEY')) {
    return true;
  }

  return normalizedValidationId === 2 &&
    getAdditionalDataParameterName(additionalData) === 'SKEY' &&
    (
      upperMessage.includes('MISSING') ||
      upperMessage.includes('NOT DEFINED') ||
      upperMessage.includes('NO VALUE') ||
      upperMessage.includes('NO VALUES') ||
      upperMessage.includes('IS EMPTY') ||
      upperMessage.includes('EMPTY')
    );
}

function hasOptionalEtalonSkey(
  normalizedValidationId: number,
  message: string,
  upperMessage: string,
  additionalData?: Record<string, string>
): boolean {
  if (!isMissingSkeyIssue(normalizedValidationId, upperMessage, additionalData)) {
    return false;
  }

  const objectType = extractIssueObjectType(message, additionalData);
  return isKnownObjectType(objectType) && !isSkeyRequiredForObjectType(objectType);
}

function hasResolvableEtalonEndTypes(
  normalizedValidationId: number,
  message: string,
  upperMessage: string,
  additionalData?: Record<string, string>
): boolean {
  if (normalizedValidationId !== 33 || !upperMessage.includes('END-TYPE')) {
    return true;
  }

  const objectType = extractIssueObjectType(message, additionalData);
  if (!isKnownObjectType(objectType)) {
    return true;
  }

  const skey = extractIssueSkey(message, additionalData);
  return hasEtalonEndTypeExpectation(objectType, skey);
}

function isMappingConfigurationIssue(
  validationId: number,
  upperMessage: string
): boolean {
  const normalizedValidationId = normalizeValidationId(validationId);

  if (normalizedValidationId !== 34) {
    return false;
  }

  if (isValueCoverageIssue(normalizedValidationId, upperMessage)) {
    return false;
  }

  return (
    upperMessage.includes('REQUIRED MAPPING') ||
    upperMessage.includes('MAPPED PARAMETER') ||
    upperMessage.includes('TARGET REVIT PARAMETER') ||
    upperMessage.includes('FORMULA') ||
    upperMessage.includes('NOT FOUND IN THIS MODEL') ||
    upperMessage.includes('NOT FOUND ON PIPING ELEMENTS')
  );
}

function getGenericFallbackFixes(
  message: string,
  additionalData?: Record<string, string>,
  elementIdsCount = 0
): QuickFixConfig[] {
  if (!additionalData || elementIdsCount <= 0) {
    return [];
  }

  const parameterName =
    additionalData.parameterName?.trim() ||
    additionalData.currentEndTypeParameterName?.trim() ||
    additionalData.objectTypeParameterName?.trim() ||
    additionalData.skeyParameterName?.trim() ||
    '';

  if (!parameterName) {
    return [];
  }

  const messageClass = classifyIssueMessage(message);
  const looksFixableBySetParameter = messageClass !== 'other';

  if (!looksFixableBySetParameter) {
    return [];
  }

  const normalizedParameter = parameterName.trim().toUpperCase();
  const isObjectType = normalizedParameter === 'OBJECT-TYPE';
  const actionVerb = messageClass === 'missing' ? 'Add' : 'Update';
  const label = `${actionVerb} ${parameterName} value`;
  const description = messageClass === 'missing'
    ? `Add missing value for mapped parameter '${parameterName}'`
    : `Update invalid value for mapped parameter '${parameterName}'`;

  if (isObjectType) {
    return [
      {
        label,
        type: 'dropdown',
        parameterName,
        canAutoFix: false,
        fixType: 'setParameter',
        description,
        options: OBJECT_TYPE_OPTIONS,
      },
    ];
  }

  return [
    {
      label,
      type: 'input',
      parameterName,
      canAutoFix: false,
      fixType: 'setParameter',
      description,
      placeholder: `${actionVerb} value for ${parameterName}`,
    },
  ];
}

export function getQuickFixesForIssue(
  validationId: number,
  message: string,
  additionalData?: Record<string, string>,
  elementIdsCount = 0
): QuickFixConfig[] {
  const normalizedValidationId = normalizeValidationId(validationId);
  const upperMessage = message.toUpperCase();
  const messageClass = classifyIssueMessage(message);

  if (hasOptionalEtalonSkey(normalizedValidationId, message, upperMessage, additionalData)) {
    return [];
  }

  if (!hasResolvableEtalonEndTypes(normalizedValidationId, message, upperMessage, additionalData)) {
    return [];
  }

  const fixes = quickFixMap[normalizedValidationId] || [];
  const filtered = fixes.filter((fix) => {
    if (fix.requiresElements && elementIdsCount <= 0) {
      return false;
    }
    if (!fix.messageFilter) {
      return true;
    }
    return upperMessage.includes(fix.messageFilter.toUpperCase());
  });

  if (filtered.length > 0) {
    return filtered.map((fix) => adaptFixToIssueContext(
      fix,
      normalizedValidationId,
      messageClass,
      additionalData
    ));
  }

  if (isMappingConfigurationIssue(validationId, upperMessage)) {
    return [];
  }

  return getGenericFallbackFixes(message, additionalData, elementIdsCount);
}

export function hasQuickFix(validationId: number): boolean {
  const normalizedValidationId = normalizeValidationId(validationId);
  return !!quickFixMap[normalizedValidationId] && quickFixMap[normalizedValidationId].length > 0;
}

export function getAutoFixConfig(validationId: number): QuickFixConfig | undefined {
  const fixes = quickFixMap[normalizeValidationId(validationId)];
  if (!fixes) {
    return undefined;
  }
  return fixes.find((fix) => fix.canAutoFix);
}
